/**
 * Facebook Pixel Tracking Utilities
 * 
 * Provides type-safe functions for tracking Facebook Pixel events
 * with product data from Shopify.
 * 
 * Currency is locale-aware: pass the current locale's currencyCode
 * and locale price so FB receives the value the user actually sees.
 */

// Declare fbq and klaviyo on window for TypeScript
declare global {
  interface Window {
    fbq: (
      action: string,
      event: string,
      params?: Record<string, unknown>
    ) => void;
    klaviyo: {
      identify: (properties: Record<string, unknown>, callback?: () => void) => void;
      track: (event: string, properties?: Record<string, unknown>) => void;
      push: (args: unknown[]) => void;
    };
  }
}

// Product data interface
export interface ProductData {
  id: string;           // Shopify product/variant ID
  name: string;         // Product name
  price: number;        // Price in the locale currency the user sees
  currency: string;     // Currency code (USD, GBP, EUR, AUD)
  quantity?: number;    // Quantity (default: 1)
  size?: string;        // Size variant (Standard/King)
}

/**
 * Helper to safely call fbq with retry if not yet loaded
 */
function safeFbq(action: string, event: string, params: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  
  const tryTrack = () => {
    if (window.fbq) {
      window.fbq(action, event, params);
      console.log(`[FB Pixel] ${event} tracked:`, params);
      return true;
    }
    return false;
  };
  
  // Try immediately
  if (tryTrack()) return;
  
  // Retry after a short delay (FB Pixel loads on window.load)
  setTimeout(() => {
    if (!tryTrack()) {
      // Final retry after longer delay
      setTimeout(tryTrack, 2000);
    }
  }, 500);
}

/**
 * Helper to safely call Klaviyo's track/identify with retry if not yet loaded.
 * Klaviyo.js uses a queue-based API so calls before load are buffered automatically,
 * but we still guard against undefined for safety.
 */
function safeKlaviyo(method: 'track' | 'identify', ...args: unknown[]): void {
  if (typeof window === 'undefined') return;

  const tryCall = () => {
    if (window.klaviyo && typeof window.klaviyo[method] === 'function') {
      (window.klaviyo[method] as (...a: unknown[]) => void)(...args);
      console.log(`[Klaviyo] ${method} called:`, ...args);
      return true;
    }
    return false;
  };

  if (tryCall()) return;
  setTimeout(() => { if (!tryCall()) setTimeout(tryCall, 2000); }, 500);
}

/**
 * Track ViewContent event when user views a product
 * Called on page load for product landing pages
 */
export function trackViewContent(product: ProductData): void {
  safeFbq('track', 'ViewContent', {
    content_ids: [product.id],
    content_name: product.name,
    content_type: 'product',
    value: product.price,
    currency: product.currency,
  });

  // Klaviyo: Viewed Product (follows Klaviyo's standard ecommerce event spec)
  safeKlaviyo('track', 'Viewed Product', {
    ProductName: product.name,
    ProductID: product.id,
    Price: product.price,
    Currency: product.currency,
    URL: typeof window !== 'undefined' ? window.location?.href : '',
  });
}

/**
 * Track AddToCart event when user clicks Order Now / Add to Cart
 * Called when user initiates checkout (before redirect to Shopify)
 */
export function trackAddToCart(product: ProductData): void {
  safeFbq('track', 'AddToCart', {
    content_ids: [product.id],
    content_name: product.name,
    content_type: 'product',
    value: product.price,
    currency: product.currency,
    num_items: product.quantity || 1,
  });

  // Klaviyo: Added to Cart
  safeKlaviyo('track', 'Added to Cart', {
    ProductName: product.name,
    ProductID: product.id,
    Price: product.price,
    Currency: product.currency,
    Quantity: product.quantity || 1,
    URL: typeof window !== 'undefined' ? window.location?.href : '',
  });
}

/**
 * Track InitiateCheckout event
 * Called when user is about to be redirected to Shopify checkout
 */
export function trackInitiateCheckout(products: ProductData[], totalValue: number, currency: string = 'USD'): void {
  safeFbq('track', 'InitiateCheckout', {
    content_ids: products.map(p => p.id),
    content_type: 'product',
    value: totalValue,
    currency,
    num_items: products.reduce((sum, p) => sum + (p.quantity || 1), 0),
  });

  // Klaviyo: Started Checkout
  safeKlaviyo('track', 'Started Checkout', {
    $value: totalValue,
    Currency: currency,
    ItemNames: products.map(p => p.name),
    Items: products.map(p => ({
      ProductID: p.id,
      ProductName: p.name,
      Quantity: p.quantity || 1,
      ItemPrice: p.price,
    })),
  });
}

// Shopify variant IDs for FluffCo pillows
export const SHOPIFY_VARIANT_IDS = {
  standard: {
    1: '44533282324679', // 1x Standard
    2: '44533282357447', // 2x Standard
    4: '44533282390215', // 4x Standard
  },
  king: {
    1: '44533282422983', // 1x King
    2: '44533282455751', // 2x King
    4: '44533282488519', // 4x King
  },
} as const;

// Shopify variant IDs for upsell products
export const UPSELL_VARIANT_IDS = {
  pillowcase_standard: '52100961337638',
  pillowcase_king: '52100961370406',
  silk_eyemask: '52098096890150',
  extra_pillow_standard: '52010146758950',
  extra_pillow_king: '52010147479846',
  shipping_insurance: '50283956896038',
} as const;

// USD fallback pricing data (used when locale pricing is not available)
// These should match the current prices in useLocalePricing.ts
export const PRODUCT_PRICES = {
  standard: {
    1: 55.90,
    2: 85.90,
    4: 129.90,
  },
  king: {
    1: 55.90,
    2: 85.90,
    4: 129.90,
  },
} as const;

/**
 * Describes a single item in the checkout cart for FB Pixel tracking.
 */
export interface CartItem {
  id: string;
  name: string;
  price: number;       // locale-aware price
  quantity: number;
}

/**
 * Track AddToCart with the full cart contents.
 * Fires a single AddToCart event containing all content_ids and the cart total.
 * Called whenever the cart changes (upsell add/remove, insurance toggle, qty change).
 */
export function trackCartAddToCart(items: CartItem[], currency: string = 'USD'): void {
  if (items.length === 0) return;
  const totalValue = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const contentIds = items.map(item => item.id);
  const contentNames = items.map(item => item.name);
  safeFbq('track', 'AddToCart', {
    content_ids: contentIds,
    content_name: contentNames.join(', '),
    content_type: 'product',
    value: Math.round(totalValue * 100) / 100,
    currency,
    num_items: totalItems,
  });

  // Klaviyo: Added to Cart (full cart update)
  safeKlaviyo('track', 'Added to Cart', {
    $value: Math.round(totalValue * 100) / 100,
    Currency: currency,
    ItemNames: contentNames,
    Items: items.map(item => ({
      ProductID: item.id,
      ProductName: item.name,
      Quantity: item.quantity,
      ItemPrice: item.price,
    })),
  });
}

/**
 * Build the full cart items array from the current checkout state.
 * This is a pure helper — call it from the component and pass to trackCartAddToCart.
 */
export function buildCartItems(opts: {
  size: 'standard' | 'king';
  quantity: 1 | 2 | 4;
  mainPrice: number;
  pillowcaseSelected: boolean;
  pillowcaseQuantity: number;
  pillowcasePrice: number;       // price per 2-pack
  eyemaskSelected: boolean;
  eyemaskPrice: number;
  extraPillowSelected: boolean;
  extraPillowPrice: number;
  insuranceSelected: boolean;
  insurancePrice: number;
}): CartItem[] {
  const items: CartItem[] = [];
  const sizeKey = opts.size;
  const qty = opts.quantity;

  // Main product
  items.push({
    id: SHOPIFY_VARIANT_IDS[sizeKey][qty],
    name: `FluffCo Down Pillow - ${sizeKey === 'standard' ? 'Standard' : 'King'} - ${qty}x`,
    price: opts.mainPrice,
    quantity: 1, // The variant already encodes the pack quantity
  });

  // Pillowcase set upsell
  if (opts.pillowcaseSelected && opts.pillowcaseQuantity > 0) {
    items.push({
      id: UPSELL_VARIANT_IDS[`pillowcase_${sizeKey}`],
      name: `Pillow Cases 2-Pack (${sizeKey === 'standard' ? 'Standard' : 'King'})`,
      price: opts.pillowcasePrice,
      quantity: opts.pillowcaseQuantity,
    });
  }

  // Silk eyemask upsell
  if (opts.eyemaskSelected) {
    items.push({
      id: UPSELL_VARIANT_IDS.silk_eyemask,
      name: 'Silk Eyemask',
      price: opts.eyemaskPrice,
      quantity: 1,
    });
  }

  // Extra pillow upsell
  if (opts.extraPillowSelected) {
    items.push({
      id: UPSELL_VARIANT_IDS[`extra_pillow_${sizeKey}`],
      name: `FluffCo ${sizeKey === 'standard' ? 'Standard' : 'King'} Pillow (Bundle)`,
      price: opts.extraPillowPrice,
      quantity: 1,
    });
  }

  // Shipping insurance
  if (opts.insuranceSelected && opts.insurancePrice > 0) {
    items.push({
      id: UPSELL_VARIANT_IDS.shipping_insurance,
      name: 'Shipping Protection',
      price: opts.insurancePrice,
      quantity: 1,
    });
  }

  return items;
}

/**
 * Helper to get product data for tracking.
 * 
 * @param size - 'standard' or 'king'
 * @param quantity - 1, 2, or 4
 * @param localePrice - The price the user actually sees (in their locale currency).
 *                       Falls back to USD price if not provided.
 * @param currencyCode - The currency code the user sees (e.g. 'GBP'). Defaults to 'USD'.
 */
export function getProductDataForTracking(
  size: 'standard' | 'king',
  quantity: 1 | 2 | 4,
  localePrice?: number,
  currencyCode: string = 'USD'
): ProductData {
  const variantId = SHOPIFY_VARIANT_IDS[size][quantity];
  const price = localePrice ?? PRODUCT_PRICES[size][quantity];
  const sizeName = size === 'standard' ? 'Standard (20"×28")' : 'King (20"×36")';
  
  return {
    id: variantId,
    name: `FluffCo Down Pillow - ${sizeName} - ${quantity}x`,
    price,
    currency: currencyCode,
    quantity,
    size: sizeName,
  };
}
